import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

/**
 * Verifica se o erro é um erro de DOM causado por extensões de navegador
 * (tradutores automáticos, ad blockers, etc.)
 *
 * Esses erros ocorrem quando extensões modificam o DOM e o React tenta
 * atualizar nós que foram movidos/removidos externamente.
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message?.toLowerCase() || '';
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('dynamically imported module') ||
    message.includes('loading chunk') ||
    error.name === 'ChunkLoadError'
  );
}

function isDOMManipulationError(error: Error): boolean {
  const errorMessage = error.message?.toLowerCase() || '';
  const domErrors = [
    'failed to execute \'removechild\' on \'node\'',
    'failed to execute \'insertbefore\' on \'node\'',
    'failed to execute \'appendchild\' on \'node\'',
    'the node to be removed is not a child of this node',
    'the node before which the new node is to be inserted is not a child',
  ];

  return domErrors.some(domError => errorMessage.includes(domError));
}

export function ErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const [autoRecoveryAttempted, setAutoRecoveryAttempted] = useState(false);
  const isDomError = isDOMManipulationError(error);
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    // Para erros de chunk (novo deploy), forçar reload para pegar os novos assets
    if (isChunkError && !autoRecoveryAttempted) {
      console.warn('[ErrorBoundary] Erro de chunk detectado (novo deploy). Recarregando a página...');
      setAutoRecoveryAttempted(true);
      const timer = setTimeout(() => {
        window.location.reload();
      }, 1500);
      return () => clearTimeout(timer);
    }

    // Para erros de DOM causados por extensões, tentar recuperação automática
    if (isDomError && !autoRecoveryAttempted) {
      console.warn('[ErrorBoundary] Erro de DOM detectado (possivelmente causado por extensão de navegador). Tentando recuperação automática...');

      // Registrar como warning no Sentry em vez de erro crítico
      Sentry.addBreadcrumb({
        category: 'dom',
        message: 'DOM manipulation error - auto recovery attempted',
        level: 'warning',
        data: {
          errorMessage: error.message,
          possibleCause: 'Browser extension (translator, ad blocker, etc.)',
        },
      });

      setAutoRecoveryAttempted(true);

      // Tentar recuperação automática após um breve delay
      const timer = setTimeout(() => {
        resetErrorBoundary();
      }, 500);

      return () => clearTimeout(timer);
    }

    // Para outros erros, capturar normalmente no Sentry
    if (!isDomError) {
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'ErrorBoundary');
        scope.setLevel('error');
        scope.setContext('Error Boundary', {
          componentStack: (error as any).componentStack,
          errorBoundary: true,
        });
        Sentry.captureException(error);
      });
    }
  }, [error, isDomError, autoRecoveryAttempted, resetErrorBoundary]);

  // Para erros de chunk (novo deploy detectado), recarregar automaticamente
  if (isChunkError) {
    return (
      <div
        className="flex h-screen w-screen flex-col items-center justify-center bg-gray-50 text-gray-800"
        role="alert"
      >
        <div className="mx-auto max-w-lg rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-4 flex justify-center">
            <LoadingIndicator type="dot-circle" size="lg" />
          </div>
          <h2 className="mb-4 text-xl font-semibold text-gray-700">
            Nova versão disponível!
          </h2>
          <p className="mb-4 text-sm text-gray-600">
            O sistema foi atualizado. A página será recarregada automaticamente.
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Recarregar agora
          </Button>
        </div>
      </div>
    );
  }

  // Para erros de DOM, mostrar uma mensagem mais amigável e tentar recuperar
  if (isDomError) {
    return (
      <div
        className="flex h-screen w-screen flex-col items-center justify-center bg-gray-50 text-gray-800"
        role="alert"
      >
        <div className="mx-auto max-w-lg rounded-lg bg-white p-8 text-center shadow-lg">
          <div className="mb-4 flex justify-center">
            <LoadingIndicator type="dot-circle" size="lg" />
          </div>
          <h2 className="mb-4 text-xl font-semibold text-gray-700">
            Atualizando a página...
          </h2>
          <p className="mb-4 text-sm text-gray-600">
            Detectamos um conflito de renderização. Isso pode ser causado por extensões do navegador
            (como tradutores automáticos). A página será atualizada automaticamente.
          </p>
          <p className="text-xs text-gray-500">
            Se o problema persistir, tente desativar extensões que modificam o conteúdo da página.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-screen w-screen flex-col items-center justify-center bg-gray-100 text-gray-800"
      role="alert"
    >
      <div className="mx-auto max-w-lg rounded-lg bg-white p-8 text-center shadow-lg">
        <h2 className="mb-4 text-2xl font-bold text-red-600">
          Oops! Algo deu errado.
        </h2>
        <p className="mb-6">
          Encontramos um erro inesperado. Nossa equipe já foi notificada.
        </p>
        <pre className="mb-6 whitespace-pre-wrap rounded-md bg-gray-50 p-4 text-left text-sm text-red-700">
          {error.message}
        </pre>
        <Button
          onClick={resetErrorBoundary}
          className="bg-blue-600 text-white hover:bg-blue-700"
        >
          Tentar Novamente
        </Button>
      </div>
    </div>
  );
}
